// src/modules/settings/TenantSettings.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  getAllTenantSettings,
  updateAllTenantSettings,
  DefatomAgentSettings,
  RemediationSettings,
  ComplianceSettings,
  PatchBySeverityConfiguration,
  ReportExpiryMinutes,
  ReportsSettings,
  FeatureListEntry,
  getCustomerName,
} from '../../components/services/TenantSettingsService';
import { useAllGroups } from '../../components/services/dashboardService';
import { DEFAULT_SPLASHTOP_SETTINGS } from './constants/settingsDefaults';
import { TenantGroupOption, buildRemoteAccessFeatureList, toTenantGroupOptions } from './helpers/remoteAccessSettings';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { toInt } from '../../common/Utils/numberHelpers';
import { deepClone } from '../../common/Utils/objectHelpers';
import TenantSettingsEditForm from './TenantSettingsEditForm';
import TenantSettingsView from './TenantSettingsView';

interface TenantData {
  id: string;
  tenantId: string;
  tenantName: string;
  settings: {
    defatomAgent: DefatomAgentSettings;
    remediation: RemediationSettings;
    complaince: ComplianceSettings;
    patchBySeverityConfiguration: PatchBySeverityConfiguration;
    reports: ReportsSettings;
    /** null when it couldn't be read; remote access is then shown as unavailable and featureList is never saved. */
    featureList: FeatureListEntry[] | null;
  };
}

const TenantSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();

  const searchParams = new URLSearchParams(location.search);
  const editMode = searchParams.get('edit') === 'true';
  const { selectedTenantId } = useTenantSelection();
  const tenantId = selectedTenantId || '';

  const [tenantData, setTenantData] = useState<TenantData | null>(null);
  const [originalTenantData, setOriginalTenantData] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Tenant groups for the remote access group limit; null when they couldn't be loaded.
  const [tenantGroups, setTenantGroups] = useState<TenantGroupOption[] | null>(null);
  const { getGroups } = useAllGroups();

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    defatomAgent: true,
    remediation: true,
    compliance: true,
    patchSeverity: true,
    reportTiming: true,
    remoteAccess: true,
  });

  const [isEditMode, setIsEditMode] = useState(editMode);

  useEffect(() => {
    setIsEditMode(editMode);
  }, [editMode]);

  // Fetch tenant settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      if (!tenantId) {
        setLoadError('Tenant ID not found. Please select a tenant.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setLoadError(null);

        // Fetch tenant display name from API FIRST
        let displayName = 'Tenant'; // fallback
        try {
          const customerName = await getCustomerName(tenantId as string);
          if (customerName && customerName.trim() !== '') {
            displayName = customerName;
            console.log('Customer Name:', displayName);
          } else {
            console.warn('getCustomerName returned empty string');
          }
        } catch (nameError: any) {
          console.error('Error fetching customer name:', nameError);
          setLoadError(`Failed to fetch tenant name: ${nameError?.message || 'Unknown error'}`);
          setLoading(false);
          return; // Stop here if tenant name fetch fails
        }

        // Now fetch the settings
        const settings = await getAllTenantSettings(tenantId as string);

        // Validate the response data structure
        if (!settings || typeof settings !== 'object') {
          throw new Error('Invalid settings data received from server');
        }

        // Ensure all required sections exist with fallbacks
        const safeSettings = {
          defatomAgent: settings.defatomAgent || {},
          remediation: settings.remediation || {},
          complaince: settings.complaince || {},
          patchBySeverityConfiguration: settings.patchBySeverityConfiguration || { severityCVEMapping: [], daysRange: [] },
          reports: {
            ReportListingThresholdInDays: settings.reports?.ReportListingThresholdInDays || 0,
            ReportExpiryMinutes: settings.reports?.ReportExpiryMinutes || {},
          },
          featureList: settings.featureList,
        };

        // Validate critical data structures
        if (!Array.isArray(safeSettings.patchBySeverityConfiguration.severityCVEMapping)) {
          safeSettings.patchBySeverityConfiguration.severityCVEMapping = [];
        }
        if (!Array.isArray(safeSettings.patchBySeverityConfiguration.daysRange)) {
          safeSettings.patchBySeverityConfiguration.daysRange = [];
        }

        const loadedData = {
          id: tenantId as string,
          tenantId: tenantId as string,
          tenantName: displayName,
          settings: safeSettings as any,
        };
        setTenantData(loadedData);
        setOriginalTenantData(deepClone(loadedData));
      } catch (error: any) {
        console.error('Error fetching settings:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load tenant settings';
        setLoadError(errorMessage);
        addToast(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [tenantId, addToast]); // Added addToast to dependency array

  // Only used for naming groups in the remote access settings, so a failure doesn't block the page.
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setTenantGroups(null);
    getGroups(tenantId)
      .then((response) => {
        if (!cancelled) setTenantGroups(toTenantGroupOptions(response));
      })
      .catch((error) => console.error('Error fetching tenant groups:', error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleEditClick = () => {
    navigate('/settings?edit=true');
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    navigate('/settings');
    setIsEditMode(false);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleSubmit = async (values: any, { setSubmitting, setErrors }: any) => {
    if (!tenantData || !originalTenantData) {
      addToast('Tenant data not available', 'error');
      return;
    }

    try {
      setSaving(true);

      // Validate data before submission
      if (!values.severityCVEMapping || !Array.isArray(values.severityCVEMapping) || values.severityCVEMapping.length !== 4) {
        throw new Error('Invalid severity CVE mapping data');
      }
      if (!values.daysRange || !Array.isArray(values.daysRange) || values.daysRange.length !== 4) {
        throw new Error('Invalid days range data');
      }

      // Build update payload with only changed values
      const updatePayload: {
        defatomAgent?: Partial<DefatomAgentSettings>;
        remediation?: RemediationSettings;
        complaince?: ComplianceSettings;
        patchBySeverityConfiguration?: PatchBySeverityConfiguration;
        reports?: {
          ReportListingThresholdInDays?: number;
          ReportExpiryMinutes?: Partial<ReportExpiryMinutes>;
        };
        featureList?: FeatureListEntry[];
      } = {};

      // Check defatomAgent changes
      const defatomAgentChanges: Partial<DefatomAgentSettings> = {};
      const newDefatomValues = {
        numberOfDaysForIdentifyingDisconncetedEndpoint: toInt(
          values.numberOfDaysForIdentifyingDisconncetedEndpoint,
          tenantData.settings.defatomAgent.numberOfDaysForIdentifyingDisconncetedEndpoint,
        ),
        maxEndpointDisconnectedThresholdInDays: toInt(
          values.maxEndpointDisconnectedThresholdInDays,
          tenantData.settings.defatomAgent.maxEndpointDisconnectedThresholdInDays,
        ),
        thresholdInMinutesForTaggingAgentAsOffline: toInt(
          values.thresholdInMinutesForTaggingAgentAsOffline,
          tenantData.settings.defatomAgent.thresholdInMinutesForTaggingAgentAsOffline,
        ),
        thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan: toInt(
          values.thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan,
          tenantData.settings.defatomAgent.thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan,
        ),
        useVendorPackageRepoAsPrimary: values.useVendorPackageRepoAsPrimary, // Add this line
      };

      Object.entries(newDefatomValues).forEach(([key, value]) => {
        const origValue = (originalTenantData.settings.defatomAgent as any)?.[key];
        if (value !== origValue) {
          (defatomAgentChanges as any)[key] = value;
        }
      });

      if (Object.keys(defatomAgentChanges).length > 0) {
        updatePayload.defatomAgent = defatomAgentChanges;
      }

      // Check remediation changes
      const newRemediationValue = toInt(
        values.numberOfSoftwareVersionsAvailableForRemediation,
        tenantData.settings.remediation.numberOfSoftwareVersionsAvailableForRemediation,
      );
      if (newRemediationValue !== originalTenantData.settings.remediation.numberOfSoftwareVersionsAvailableForRemediation) {
        updatePayload.remediation = {
          numberOfSoftwareVersionsAvailableForRemediation: newRemediationValue,
        };
      }

      // Check compliance changes
      const complianceChanges: Partial<ComplianceSettings> = {};
      const newComplianceValues = {
        currentPreviousDurationInDays: toInt(
          values.currentPreviousDurationInDays,
          tenantData.settings.complaince.currentPreviousDurationInDays,
        ),
        nonComplainceDefinitionDays: toInt(values.nonComplainceDefinitionDays, tenantData.settings.complaince.nonComplainceDefinitionDays),
      };

      Object.entries(newComplianceValues).forEach(([key, value]) => {
        const origValue = (originalTenantData.settings.complaince as any)?.[key];
        if (value !== origValue) {
          (complianceChanges as any)[key] = value;
        }
      });

      if (Object.keys(complianceChanges).length > 0) {
        updatePayload.complaince = complianceChanges as ComplianceSettings;
      }

      // Check patch severity configuration changes (granular)
      const patchSeverityChanges: Partial<PatchBySeverityConfiguration> = {};

      // Check severityCVEMapping
      const originalCVEMapping = JSON.stringify(originalTenantData.settings.patchBySeverityConfiguration.severityCVEMapping);
      const newCVEMapping = JSON.stringify(values.severityCVEMapping);
      if (originalCVEMapping !== newCVEMapping) {
        patchSeverityChanges.severityCVEMapping = values.severityCVEMapping;
      }

      // Check daysRange
      const originalDaysRange = JSON.stringify(originalTenantData.settings.patchBySeverityConfiguration.daysRange);
      const newDaysRange = JSON.stringify(values.daysRange);
      if (originalDaysRange !== newDaysRange) {
        patchSeverityChanges.daysRange = values.daysRange;
      }

      if (Object.keys(patchSeverityChanges).length > 0) {
        updatePayload.patchBySeverityConfiguration = patchSeverityChanges as PatchBySeverityConfiguration;
      }

      // Check report expiry timing changes (dynamic keys)
      const currentReportExpiry = (values.reportExpiryMinutes || {}) as Record<string, number>;
      const originalReportExpiry = (originalTenantData?.settings?.reports?.ReportExpiryMinutes || {}) as Record<string, number>;

      const changedReportExpiry = getChangedReportExpiryMinutes(currentReportExpiry, originalReportExpiry);

      const currentReportListingThresholdInDays = toInt(
        values.reportListingThresholdInDays,
        tenantData.settings.reports.ReportListingThresholdInDays,
      );
      const originalReportListingThresholdInDays = originalTenantData.settings.reports.ReportListingThresholdInDays;

      const reportsChanges: {
        ReportListingThresholdInDays?: number;
        ReportExpiryMinutes?: Partial<ReportExpiryMinutes>;
      } = {};

      if (currentReportListingThresholdInDays !== originalReportListingThresholdInDays) {
        reportsChanges.ReportListingThresholdInDays = currentReportListingThresholdInDays;
      }

      if (Object.keys(changedReportExpiry).length > 0) {
        reportsChanges.ReportExpiryMinutes = changedReportExpiry;
      }

      if (Object.keys(reportsChanges).length > 0) {
        updatePayload.reports = reportsChanges;
      }

      // Remote access (Splashtop) settings. featureList is saved whole, built from the list that was loaded with only
      // the Splashtop entry changed; skipped when nothing changed or the list couldn't be loaded.
      const featureList = buildRemoteAccessFeatureList(
        originalTenantData.settings.featureList,
        values.remoteAccess,
        DEFAULT_SPLASHTOP_SETTINGS,
      );
      if (featureList) {
        updatePayload.featureList = featureList;
      }

      // Check if there are any changes to send
      if (Object.keys(updatePayload).length === 0) {
        addToast('No changes detected', 'info');
        navigate('/settings');
        setIsEditMode(false);
        return;
      }

      // Use new bulk update API
      const success = await updateAllTenantSettings(tenantId, tenantData.tenantName, updatePayload);

      if (success) {
        // Refresh the data
        const settings = await getAllTenantSettings(tenantId);
        const updatedData = {
          ...tenantData,
          settings,
        };
        setTenantData(updatedData);
        setOriginalTenantData(deepClone(updatedData));

        addToast('Settings updated successfully', 'success');
        navigate('/settings');
        setIsEditMode(false);
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error: any) {
      console.error('Error updating settings:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update settings';
      addToast(errorMessage, 'error');

      // Set form-level error if validation errors exist
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-white flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="animate-spin text-red-500" size={48} />
          <p className="text-gray-400">Loading tenant settings...</p>
        </div>
      </div>
    );
  }

  if (loadError || !tenantData) {
    return (
      <div className="p-6 text-white flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h3 className="text-xl font-semibold mb-2">Failed to Load Settings</h3>
          <p className="text-gray-400 mb-4">{loadError || 'Unable to load tenant settings. Please try again.'}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#b31317] text-white rounded-lg hover:bg-red-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isEditMode) {
    return (
      <TenantSettingsEditForm
        tenantData={tenantData}
        onSubmit={handleSubmit}
        onCancel={handleCancelClick}
        saving={saving}
        tenantGroups={tenantGroups}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
      />
    );
  }

  return (
    <TenantSettingsView
      tenantData={tenantData}
      onEdit={handleEditClick}
      tenantGroups={tenantGroups}
      expandedSections={expandedSections}
      toggleSection={toggleSection}
    />
  );
};

export default TenantSettings;

// helper: get only changed report expiry keys
const getChangedReportExpiryMinutes = (current: Record<string, number>, original: Record<string, number>): Record<string, number> => {
  const changed: Record<string, number> = {};

  Object.entries(current || {}).forEach(([key, value]) => {
    const currentValue = Number(value);
    const originalValue = Number(original?.[key]);

    if (Number.isFinite(currentValue) && currentValue !== originalValue) {
      changed[key] = currentValue;
    }
  });

  return changed;
};
